(function(global){
  'use strict';
  const copy=value=>JSON.parse(JSON.stringify(value));
  function create({store,client,shared=global,now=()=>Date.now(),notify=()=>{},hashCart=async cart=>{
    if(!cart.token||!global.crypto?.subtle)return '';
    const bytes=await global.crypto.subtle.digest('SHA-256',new TextEncoder().encode(cart.token));
    return Array.from(new Uint8Array(bytes),byte=>byte.toString(16).padStart(2,'0')).join('');
  },withLock=task=>global.navigator?.locks?global.navigator.locks.request('occ-cart-recovery',task):task(),protectedProducts=[],protectedVariants=[]}={}){
    const identity=row=>global.OCCCartVariantCore.signature({variant_id:row.variantId,properties:row.properties});
    const total=(cart,row)=>cart.items.filter(item=>global.OCCCartVariantCore.signature(item)===identity(row)).reduce((sum,item)=>sum+item.quantity,0);
    const fail=message=>{throw new Error(message);};
    function save(row){const result=store.put(row);if(!result.ok)fail(result.message);return result.row;}
    function patch(id,changes){const result=store.update(id,changes);if(!result.ok)fail(result.message);return result.row;}
    function record(item,cart,kind){
      if(protectedProducts.includes(String(item.product_id))||protectedVariants.includes(String(item.variant_id)))fail('This item is tied to a gift or protection service and needs its original flow.');
      const reason=global.OCCCartVariantCore.protectedReason(item,cart);if(reason)fail(reason);
      const row={kind,state:'pending_remove',sourceKey:item.key,variantId:String(item.variant_id),handle:item.handle,qty:item.quantity,properties:copy(item.properties||{}),title:item.product_title||item.title||item.handle,variantTitle:item.variant_title||'',createdAt:now(),...(kind==='undo'?{expiresAt:now()+5*60*1000}:{})};
      row.beforeRemoveTotal=total(cart,row);return row;
    }
    function doneRemoval(row,cart){
      if(!row)return null;
      if(!cart.items.some(item=>item.key===row.sourceKey)&&total(cart,row)===row.beforeRemoveTotal-row.qty){
        const next=patch(row.id,{state:'ready'});notify({type:row.kind==='undo'?'undo':'saved',row:next});return next;
      }
      notify({type:'review',row,message:'Removal was not confirmed. Your saved copy is retained; review the cart.'});return row;
    }
    async function captureRemoval(key){
      if(!key)return null;
      try{
        const cart=await client.readCart(),item=cart.items.find(item=>item.key===key);
        if(!item)return null;
        return save(record(item,cart,'undo'));
      }catch(error){notify({type:'info',message:'Undo is unavailable for this removal. '+error.message});return null;}
    }
    async function exclusive(task){
      if(shared.occCartMutationBusy)fail('Another cart update is in progress. Please wait.');
      const owner={};shared.occCartMutationBusy=owner;
      try{return await withLock(task);}
      finally{if(shared.occCartMutationBusy===owner)shared.occCartMutationBusy=false;}
    }
    async function saveForLater(key){return exclusive(async()=>{
      const before=await client.readCart(),item=before.items.find(item=>item.key===key);
      if(!item)fail('This cart item has changed. Review your cart before saving it.');
      // Durable save before removal. A blocked/quota-limited browser cannot discard the only copy.
      const existing=store.list().find(row=>row.kind==='saved'&&row.sourceKey===key&&row.state==='pending_remove');
      if(existing)fail('A previous save is still awaiting cart review. Recheck that saved record before trying again.');
      const row=save(record(item,before,'saved'));
      try{
        const latest=await client.readCart(),current=latest.items.find(line=>line.key===key);
        if(!current||identity(row)!==global.OCCCartVariantCore.signature(current)||current.quantity!==row.qty)fail('This cart line changed before removal. The cart was not changed; review the saved copy and current cart.');
        const protectedMessage=global.OCCCartVariantCore.protectedReason(current,latest);
        if(protectedMessage)fail(protectedMessage);
        await client.request('cart/change.js',{id:key,quantity:0});
        const after=await client.readCart();doneRemoval(row,after);
        return {row:store.get(row.id),cart:after};
      }catch(error){notify({type:'review',row,message:'The item is saved, but removal could not be confirmed. Check your cart before continuing.'});throw error;}
    });}
    async function restore(id){return exclusive(async()=>{
      let row=store.get(id);
      if(!row||['dismissed','restored'].includes(row.state))fail('This saved item has already been handled.');
      if(row.state!=='ready')fail('This item has an unconfirmed update. Recheck the cart; it will not be added again automatically.');
      if(row.kind==='undo'&&row.expiresAt<=now())fail('The undo period has ended. You can still open the product to choose it again.');
      const product=await client.request('products/'+row.handle+'.js');
      if(protectedProducts.includes(String(product.id))||protectedVariants.includes(String(row.variantId))||Object.keys(row.properties||{}).some(key=>key.startsWith('_')))fail('This saved item is tied to a protected offer or fulfilment service. Use its original product flow. Your saved copy is kept.');
      const variant=product.variants?.find(variant=>String(variant.id)===row.variantId);
      if(!variant?.available)fail('That saved option is not currently available. Your saved copy has been kept.');
      const before=await client.readCart(),beforeTotal=total(before,row),fingerprint=await hashCart(before);
      const latest=store.get(id);
      if(!latest||latest.state!=='ready'||identity(latest)!==identity(row)||latest.qty!==row.qty)fail('This saved record changed while loading. Refresh the list before continuing.');
      // Persist the intent BEFORE add. Reloads and another tab see the barrier, not a reusable add button.
      row=patch(id,{state:'pending_add',beforeAddTotal:beforeTotal,targetTotal:beforeTotal+row.qty,cartFingerprint:fingerprint});
      try{
        await client.request('cart/add.js',{items:[{id:Number(row.variantId),quantity:row.qty,properties:copy(row.properties)}]});
        const after=await client.readCart();
        if(total(after,row)!==row.targetTotal||(fingerprint&&await hashCart(after)!==fingerprint))fail('The final cart quantities changed during the update. Review the cart before continuing.');
        patch(id,{state:'restored'});notify({type:'restored',row,cart:after});return after;
      }catch(error){
        // Keep the pending intent even if a second storage write fails. Never offer blind retries.
        store.update(id,{state:'review_add'});
        notify({type:'review',row,message:error.message+' Your saved copy is retained. Recheck the cart before any further action.'});throw error;
      }
    });}
    async function recheck(id){return exclusive(async()=>{
      const row=store.get(id);if(!row)fail('This record is no longer available.');
      const cart=await client.readCart();
      if(row.state==='pending_remove')return doneRemoval(row,cart);
      if(['pending_add','review_add'].includes(row.state)){
        if(row.cartFingerprint&&await hashCart(cart)===row.cartFingerprint&&total(cart,row)>=row.targetTotal){
          patch(id,{state:'restored'});notify({type:'restored',row,cart});return {confirmed:true};
        }
        fail('The previous add cannot be confirmed. Review the cart manually; no repeat add has been sent. Your saved record remains available.');
      }
      return row;
    });}
    return {captureRemoval,confirmRemoval(row,cart){try{return doneRemoval(row,cart);}catch(error){notify({type:'review',row,message:'The cart changed, but the recovery record could not be updated. '+error.message});return row;}},saveForLater,restore,recheck,store};
  }
  global.OCCCartRecoveryCore={create};
})(typeof window==='undefined'?globalThis:window);
